/**
 * Auth gate for every in-app route.
 *
 * Carried over from ReportGen minus the report job store: on session loss it
 * clears the module-level query cache (a client-side navigate would otherwise
 * leave one officer's case data readable on the login screen) and remembers the
 * requested path so login returns the officer there.
 */
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { Box, CircularProgress } from "@mui/material";
import { MainComponent } from "./MainComponent";
import { LayoutProvider } from "../../context/LayoutContext";
import { queryClient } from "../../shared/api/queryClient";
import { savePostLoginRedirect } from "../../shared/auth/postLoginRedirect";

export function ProtectedView({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (isLoading) return;

    if (!user && !hasRedirected.current) {
      hasRedirected.current = true;
      console.warn("[ProtectedView] Not authenticated, redirecting to login");
      queryClient.clear();
      savePostLoginRedirect(location.pathname + location.search + location.hash);
      navigate("/login", { replace: true });
    }
  }, [isLoading, user, navigate, location]);

  useEffect(() => {
    if (user) hasRedirected.current = false;
  }, [user]);

  if (isLoading || !user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LayoutProvider>
      <MainComponent>{children}</MainComponent>
    </LayoutProvider>
  );
}

export default ProtectedView;
